import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { QRCodeCanvas } from "qrcode.react";
import { QrCode, Plus, Ban, RefreshCw, Printer, ShieldCheck, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { publicOrigin, shareOrPrintQr } from "@/lib/qrShare";

function qrUrl(token) {
  return `${publicOrigin()}/q/${token}`;
}

function TokenModal({ result, onClose }) {
  if (!result) return null;
  const url = qrUrl(result.token);
  const printQr = async () => {
    const r = await shareOrPrintQr({ canvasId: "qr-modal-canvas", title: result.action_label || "Record temperature", subtitle: result.label, fileName: "caterctrl-qr.png" });
    if (r === "downloaded") toast.success("QR image saved — print it from your downloads");
    if (r === "missing") toast.error("QR not ready — please try again");
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="qr-token-modal">
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center relative max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-2rem)] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-400" data-testid="qr-token-modal-close"><X size={18} /></button>
        <ShieldCheck className="mx-auto text-emerald-500" size={28} />
        <h2 className="font-display text-lg font-bold text-slate-800 mt-2">QR code ready</h2>
        <p className="text-xs text-amber-600 mt-1 font-semibold">Shown only once — print or save it now. It cannot be retrieved again.</p>
        <div className="my-4 flex justify-center"><QRCodeCanvas id="qr-modal-canvas" value={url} size={220} includeMargin /></div>
        <div className="text-sm font-semibold text-slate-700">{result.action_label} · {result.label}</div>
        <button onClick={printQr} data-testid="qr-token-print" className="mt-4 w-full px-4 py-2.5 rounded-xl bg-brand text-white font-semibold text-sm flex items-center justify-center gap-2"><Printer size={15} /> Print or share QR</button>
      </div>
    </div>
  );
}

export default function QrCodes() {
  const [venues, setVenues] = useState([]);
  const [thresholds, setThresholds] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [site, setSite] = useState("");
  const [thresholdId, setThresholdId] = useState("");
  const [expires, setExpires] = useState("");
  const [creating, setCreating] = useState(false);
  const [tokenResult, setTokenResult] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/qr").then((r) => setRows(r.data.qr_codes || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api.get("/venues").then((r) => { setVenues(r.data); if (r.data[0]) setSite(r.data[0].venue_id); }).catch(() => {});
    api.get("/temperature-thresholds").then((r) => setThresholds(r.data || [])).catch(() => {});
    load();
  }, [load]);

  const create = async () => {
    if (!site || !thresholdId) { toast.error("Choose a site and a temperature target"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/qr", {
        workflow_type: "temperature_entry", site_id: site,
        target: { threshold_id: thresholdId },
        ...(expires ? { expires_at: new Date(expires).toISOString() } : {}),
      });
      setTokenResult({ ...data, action_label: "Record temperature" });
      setShowCreate(false); setThresholdId(""); setExpires("");
      load();
    } catch (e) { toast.error(e?.normalized?.message || "Could not create QR"); }
    finally { setCreating(false); }
  };

  const revoke = async (id) => {
    try { await api.post(`/qr/${id}/revoke`); toast.success("QR revoked"); load(); }
    catch (e) { toast.error(e?.normalized?.message || "Could not revoke"); }
  };
  const rotate = async (id) => {
    try {
      const { data } = await api.post(`/qr/${id}/rotate`);
      setTokenResult({ ...data, action_label: "Record temperature" });
      toast.success("QR rotated — previous code revoked"); load();
    } catch (e) { toast.error(e?.normalized?.message || "Could not rotate"); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="qr-codes-page">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <QrCode className="text-brand" size={22} />
          <h1 className="font-display text-2xl font-bold text-slate-800">Kitchen QR Codes</h1>
        </div>
        <button onClick={() => setShowCreate((s) => !s)} data-testid="qr-create-toggle"
          className="px-4 py-2 rounded-xl bg-brand text-white font-semibold text-sm flex items-center gap-1.5"><Plus size={15} /> New QR</button>
      </div>
      <p className="text-sm text-slate-500">Create scannable codes that open a specific temperature entry at a specific site. A QR is only an entry point — staff must still sign in and have permission to record.</p>

      {showCreate && (
        <div className="cc-card p-4 space-y-3" data-testid="qr-create-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Site</label>
              <select value={site} onChange={(e) => setSite(e.target.value)} data-testid="qr-create-site"
                className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                {venues.map((v) => <option key={v.venue_id} value={v.venue_id}>{v.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Temperature target</label>
              <select value={thresholdId} onChange={(e) => setThresholdId(e.target.value)} data-testid="qr-create-threshold"
                className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                <option value="">Select a unit…</option>
                {thresholds.map((t) => <option key={t.threshold_id} value={t.threshold_id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Expiry (optional)</label>
              <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="qr-create-expiry"
                className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <button onClick={create} disabled={creating} data-testid="qr-create-submit"
            className="px-5 py-2.5 rounded-xl bg-brand text-white font-semibold text-sm disabled:opacity-40">{creating ? "Creating…" : "Create QR"}</button>
        </div>
      )}

      {loading ? (
        <div className="cc-card p-10 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>
      ) : rows.length === 0 ? (
        <div className="cc-card p-8 text-center text-slate-400" data-testid="qr-empty">No QR codes yet.</div>
      ) : (
        <div className="cc-card divide-y divide-slate-100" data-testid="qr-list">
          {rows.map((q) => (
            <div key={q.qr_id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`qr-row-${q.qr_id}`}>
              <div>
                <div className="text-sm font-semibold text-slate-700">{q.label || "Record temperature"}</div>
                <div className="text-xs text-slate-400">{venues.find((v) => v.venue_id === q.site_id)?.name || q.site_id} · {q.workflow_type}
                  {q.expires_at ? ` · expires ${new Date(q.expires_at).toLocaleDateString("en-GB")}` : ""}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${q.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`} data-testid={`qr-status-${q.qr_id}`}>
                  {q.active ? "Active" : q.revoked_at ? "Revoked" : "Inactive"}
                </span>
                {q.active && <button onClick={() => rotate(q.qr_id)} data-testid={`qr-rotate-${q.qr_id}`} className="text-xs text-slate-500 flex items-center gap-1 hover:text-brand"><RefreshCw size={13} /> Rotate</button>}
                {q.active && <button onClick={() => revoke(q.qr_id)} data-testid={`qr-revoke-${q.qr_id}`} className="text-xs text-red-500 flex items-center gap-1 hover:text-red-600"><Ban size={13} /> Revoke</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <TokenModal result={tokenResult} onClose={() => setTokenResult(null)} />
    </div>
  );
}
